import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Package, MapPin, Warehouse, ChevronRight, X, AlertTriangle, FileSpreadsheet, ArrowDownToLine, ArrowUpFromLine, Check, ChevronsUpDown, Plus, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { NewProductDialog } from "@/components/scan/NewProductDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stock level helpers
const getStockLevel = (quantity) => {
  if (quantity === 0) return 'out';
  if (quantity <= 2) return 'low';
  return 'ok';
};

const getStockColor = (level) => {
  switch (level) {
    case 'out': return 'text-red-500';
    case 'low': return 'text-amber-500';
    case 'ok': return 'text-green-500';
    default: return '';
  }
};

const getStockBgColor = (level) => {
  switch (level) {
    case 'out': return 'bg-red-500/10 border-red-500/30';
    case 'low': return 'bg-amber-500/10 border-amber-500/30';
    case 'ok': return 'bg-green-500/10 border-green-500/30';
    default: return '';
  }
};

export default function InventoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [staleMap, setStaleMap] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || "");
  const [kategori, setKategori] = useState("");
  const [tillverkare, setTillverkare] = useState("");
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [total, setTotal] = useState(0);
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [includeZeroStock, setIncludeZeroStock] = useState(true);
  const [onlyBooked, setOnlyBooked] = useState(false);
  const [showPurchasePrices, setShowPurchasePrices] = useState(() => localStorage.getItem('show_purchase_prices') === '1');
  const [priceMap, setPriceMap] = useState({});
  const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
  const [manufacturerFilterOpen, setManufacturerFilterOpen] = useState(false);
  
  // New product dialog state
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProduct, setNewProduct] = useState({
    ean: "",
    artikelnummer: "",
    tillverkare: "",
    kategori: ""
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [createFlowEan, setCreateFlowEan] = useState(null); // Track if we're in create flow from orders

  useEffect(() => {
    // Load all initial data in parallel
    Promise.all([fetchInventory(), fetchStats(), fetchFilters()]);
    
    // Check for create parameter (from order flow)
    const createParam = searchParams.get('create');
    if (createParam) {
      setSearch(createParam);
      setCreateFlowEan(createParam);
      // Show the create product dialog with artikelnummer pre-filled
      // EAN must be entered manually - it's required
      setNewProduct({
        ean: "",
        artikelnummer: createParam.toUpperCase(),
        tillverkare: "",
        kategori: ""
      });
      // Small delay to ensure categories/manufacturers are loaded
      setTimeout(() => {
        setShowNewProductDialog(true);
      }, 300);
    }
  }, []);

  // Fetch purchase price map when the staff price toggle is on
  useEffect(() => {
    localStorage.setItem('show_purchase_prices', showPurchasePrices ? '1' : '0');
    if (showPurchasePrices) {
      axios.get(`${API}/purchasing/prices/lowest-map`)
        .then(res => setPriceMap(res.data || {}))
        .catch(() => {});
    }
  }, [showPurchasePrices]); // eslint-disable-line

  useEffect(() => {
    // Set loading immediately when search changes
    if (search) {
      setLoading(true);
    }
    const timer = setTimeout(() => {
      fetchInventory();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, kategori, tillverkare, onlyInStock, includeZeroStock, onlyBooked]);

  // Track the last search that items correspond to
  const [lastFetchedSearch, setLastFetchedSearch] = useState("");

  const fetchInventory = async () => {
    const currentSearch = search; // Capture current search value
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (kategori) params.append("kategori", kategori);
      if (tillverkare) params.append("tillverkare", tillverkare);
      if (onlyInStock) params.append("only_in_stock", "true");
      if (includeZeroStock) params.append("include_zero_stock", "true");
      if (onlyBooked) params.append("only_booked", "true");
      params.append("limit", "200");
      
      const response = await axios.get(`${API}/inventory?${params}`);
      setItems(response.data.items);
      // Flag slow-moving products (no outbound movement in 6 months) for Black Friday
      const eans = (response.data.items || []).map(i => i.ean).filter(Boolean);
      if (eans.length) {
        axios.post(`${API}/inventory/last-movements`, { eans, months: 6 })
          .then(r => setStaleMap(r.data?.results || {}))
          .catch(() => {});
      } else {
        setStaleMap({});
      }
      setTotal(response.data.total);
      setLastFetchedSearch(currentSearch); // Mark which search these results are for
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Kunde inte ladda lagret");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/inventory/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchFilters = async () => {
    try {
      const [catRes, mfgRes] = await Promise.all([
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/manufacturers`)
      ]);
      setCategories(catRes.data);
      setManufacturers(mfgRes.data);
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const hasFilters = search || kategori || tillverkare;

  const clearFilters = () => {
    setSearch("");
    setKategori("");
    setTillverkare("");
  };

  // Handle saving new product
  const handleSaveNewProduct = async () => {
    // Validate EAN (8 or 13 digits)
    const cleanEan = newProduct.ean?.replace(/\s/g, '') || '';
    if (!/^\d{8}$|^\d{13}$/.test(cleanEan)) {
      toast.error("EAN måste vara 8 eller 13 siffror");
      return;
    }
    if (!newProduct.artikelnummer?.trim()) {
      toast.error("Ange artikelnummer");
      return;
    }
    if (!newProduct.tillverkare?.trim()) {
      toast.error("Välj tillverkare");
      return;
    }
    if (!newProduct.kategori?.trim()) {
      toast.error("Välj kategori");
      return;
    }
    
    setCreatingProduct(true);
    try {
      // Create the product
      await axios.post(`${API}/inventory`, {
        ean: cleanEan,
        artikelnummer: newProduct.artikelnummer,
        tillverkare: newProduct.tillverkare,
        kategori: newProduct.kategori,
        antal: 0,
        lagerplats: ""
      });
      
      toast.success(`Produkt ${newProduct.artikelnummer} skapad!`);
      setShowNewProductDialog(false);
      
      // If we came from order flow, redirect to add price then back to orders
      if (createFlowEan) {
        // Update sessionStorage to include the new product info for the return flow
        sessionStorage.setItem('returnToOrder', JSON.stringify({
          ean: newProduct.ean,
          artikelnummer: newProduct.artikelnummer,
          fromQuickOrder: true
        }));
        
        // Navigate to prices page to add a price for the new product
        toast.info("Nu kan du lägga till pris för produkten");
        navigate(`/purchasing/prices?add=${encodeURIComponent(newProduct.ean)}`);
      } else {
        // Normal flow - just refresh and show the product
        fetchInventory();
        setNewProduct({ ean: "", artikelnummer: "", tillverkare: "", kategori: "" });
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.detail || "Kunde inte skapa produkt");
    } finally {
      setCreatingProduct(false);
    }
  };

  // Handle dialog close - check if we should abort the flow
  const handleNewProductDialogClose = (open) => {
    if (!open && createFlowEan) {
      // User cancelled the create flow, clear sessionStorage and go back
      sessionStorage.removeItem('returnToOrder');
      setCreateFlowEan(null);
    }
    setShowNewProductDialog(open);
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Warehouse className="h-6 w-6 text-primary" />
              <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="inventory-title">
                Lager
              </h1>
            </div>
            <Button variant="outline" className="gap-2" onClick={() => navigate("/black-friday")} data-testid="black-friday-btn">
              <Tag className="h-4 w-4 text-primary" />
              Black Friday
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card className="stat-tile">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold font-heading text-primary">{stats.unique_products || 0}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Unika produkter i lager</p>
                </CardContent>
              </Card>
              <Card className="stat-tile">
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold font-heading">{stats.total_quantity || 0}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Totalt i lager</p>
                </CardContent>
              </Card>
              <Card className="stat-tile">
                <CardContent className="p-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <ArrowDownToLine className="h-4 w-4 text-green-500" />
                    <p className="text-2xl font-bold font-heading text-green-500">{stats.scans_in_today || 0}</p>
                  </div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Inleverans idag</p>
                </CardContent>
              </Card>
              <Card className="stat-tile">
                <CardContent className="p-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <ArrowUpFromLine className="h-4 w-4 text-red-500" />
                    <p className="text-2xl font-bold font-heading text-red-500">{stats.scans_out_today || 0}</p>
                  </div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Utleverans idag</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters only - search moved to main */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {/* Category filter - Searchable */}
              <Popover open={categoryFilterOpen} onOpenChange={setCategoryFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[160px] justify-between" data-testid="filter-category">
                    {kategori || "Alla kategorier"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput placeholder="Sök kategori..." />
                    <CommandList>
                      <CommandEmpty>Ingen kategori hittades</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value=""
                          onSelect={() => { setKategori(""); setCategoryFilterOpen(false); }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", !kategori ? "opacity-100" : "opacity-0")} />
                          Alla kategorier
                        </CommandItem>
                        {categories.map((cat) => (
                          <CommandItem
                            key={cat}
                            value={cat}
                            onSelect={() => { setKategori(cat); setCategoryFilterOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", kategori === cat ? "opacity-100" : "opacity-0")} />
                            {cat}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Manufacturer filter - Searchable */}
              <Popover open={manufacturerFilterOpen} onOpenChange={setManufacturerFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-[160px] justify-between" data-testid="filter-manufacturer">
                    {tillverkare || "Alla tillverkare"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput placeholder="Sök tillverkare..." />
                    <CommandList>
                      <CommandEmpty>Ingen tillverkare hittades</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value=""
                          onSelect={() => { setTillverkare(""); setManufacturerFilterOpen(false); }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", !tillverkare ? "opacity-100" : "opacity-0")} />
                          Alla tillverkare
                        </CommandItem>
                        {manufacturers.map((mfg) => (
                          <CommandItem
                            key={mfg}
                            value={mfg}
                            onSelect={() => { setTillverkare(mfg); setManufacturerFilterOpen(false); }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", tillverkare === mfg ? "opacity-100" : "opacity-0")} />
                            {mfg}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                  <X className="h-4 w-4" />
                  Rensa
                </Button>
              )}
            </div>

            {/* Stock level filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 pt-2 border-t border-border/50">
              <div className="flex items-center gap-2">
                <Switch 
                  id="only-in-stock" 
                  checked={onlyInStock}
                  onCheckedChange={setOnlyInStock}
                  data-testid="only-in-stock-switch"
                />
                <Label htmlFor="only-in-stock" className="text-sm cursor-pointer">
                  Visa endast produkter i lager
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch 
                  id="only-booked" 
                  checked={onlyBooked}
                  onCheckedChange={setOnlyBooked}
                  data-testid="only-booked-switch"
                />
                <Label htmlFor="only-booked" className="text-sm cursor-pointer">
                  Visa endast bokade
                </Label>
              </div>
              
              <div className="flex items-center gap-3 sm:gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></span>
                  <span className="text-muted-foreground">Slut</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-amber-500"></span>
                  <span className="text-muted-foreground">Få</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></span>
                  <span className="text-muted-foreground">I lager</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-blue-500"></span>
                  <span className="text-muted-foreground">Bokad</span>
                </span>
              </div>

              {/* Staff-only: show purchase prices (no label) */}
              <div className="sm:ml-auto flex items-center">
                <Switch
                  checked={showPurchasePrices}
                  onCheckedChange={setShowPurchasePrices}
                  data-testid="show-purchase-prices-switch"
                  title="Visa inköpspriser"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4">
        {/* Search Field */}
        <form onSubmit={(e) => {
          e.preventDefault();
          // Don't navigate while loading or if search doesn't match fetched results
          if (loading || search !== lastFetchedSearch) {
            return;
          }
          if (items.length === 1) {
            navigate(`/inventory/${items[0].ean}`);
          } else if (items.length > 0 && search.length >= 5) {
            const exactMatch = items.find(item => 
              item.ean === search || 
              item.artikelnummer?.toLowerCase() === search.toLowerCase()
            );
            if (exactMatch) {
              navigate(`/inventory/${exactMatch.ean}`);
            } else {
              navigate(`/inventory/${items[0].ean}`);
            }
          }
        }} className="mb-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
            <Input
              placeholder="Sök EAN, artikelnummer, namn eller bokningsnummer... (tryck Enter för att öppna)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-14 h-14 text-lg border-2 border-primary/60 hover:border-primary focus:border-primary focus:ring-2 focus:ring-primary/20 bg-background shadow-md font-medium"
              data-testid="search-input"
              autoFocus
            />
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 p-0"
                onClick={() => setSearch("")}
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </div>
          {search && (loading || search !== lastFetchedSearch) && (
            <p className="text-sm text-muted-foreground mt-2 ml-2">
              Söker...
            </p>
          )}
          {search && !loading && search === lastFetchedSearch && items.length > 0 && items.length <= 5 && (
            <p className="text-sm text-primary mt-2 ml-2 font-medium">
              ↵ Tryck Enter för att öppna {items.length === 1 ? `"${items[0].produktnamn}"` : "första träffen"}
            </p>
          )}
        </form>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Laddar lager...</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center" data-testid="empty-inventory">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-heading text-xl font-bold mb-2">
              {hasFilters ? "Inga produkter matchade sökningen" : "Lagret är tomt"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {hasFilters ? "Prova att ändra dina filter eller skapa en ny produkt" : "Importera produkter för att komma igång"}
            </p>
            <div className="flex justify-center gap-3">
              {hasFilters && search && (
                <Button 
                  className="gap-2"
                  onClick={() => {
                    setNewProduct({
                      ean: search,
                      artikelnummer: search.toUpperCase(),
                      tillverkare: "",
                      kategori: ""
                    });
                    setShowNewProductDialog(true);
                  }}
                  data-testid="create-product-btn"
                >
                  <Plus className="h-4 w-4" />
                  Skapa ny produkt
                </Button>
              )}
              {!hasFilters && (
                <Link to="/import-export">
                  <Button className="gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    Gå till Import & Export
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              Visar {items.length} av {total} produkter
            </p>
            <div className="space-y-2" data-testid="inventory-list">
              {items.map((item) => {
                const totalQty = item.total_antal || item.antal || 0;
                const totalBooked = item.total_booked || 0;
                const available = item.available ?? totalQty;
                const stockLevel = getStockLevel(totalQty);
                
                return (
                <Card
                  key={item.id || item.ean}
                  className={`data-card cursor-pointer hover:border-primary/50 transition-colors border-l-4 ${
                    stockLevel === 'out' ? 'border-l-red-500' : 
                    stockLevel === 'low' ? 'border-l-amber-500' : 
                    'border-l-green-500'
                  }`}
                  onClick={() => navigate(`/inventory/${item.ean}`)}
                  data-testid={`inventory-item-${item.id || item.ean}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Tillverkare and Kategori - moved to top */}
                        {(item.tillverkare || item.kategori) && (
                          <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
                            {item.tillverkare && (
                              <Badge variant="secondary" className="text-xs">{item.tillverkare}</Badge>
                            )}
                            {item.kategori && (
                              <Badge variant="secondary" className="text-xs">{item.kategori}</Badge>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading font-bold truncate">
                            {item.artikelnummer || item.ean}
                          </h3>
                          <Badge 
                            variant="outline" 
                            className={`shrink-0 ${getStockBgColor(stockLevel)} ${getStockColor(stockLevel)}`}
                          >
                            {stockLevel === 'out' && <AlertTriangle className="h-3 w-3 mr-1" />}
                            {totalQty} st totalt
                          </Badge>
                          {totalQty > 0 && staleMap[item.ean]?.stale && (
                            <Badge
                              variant="outline"
                              className="shrink-0 bg-orange-500/10 border-orange-500/30 text-orange-500 gap-1"
                              data-testid={`stale-badge-${item.ean}`}
                              title="Ingen utgående rörelse senaste 6 månaderna"
                            >
                              <Tag className="h-3 w-3" />
                              {staleMap[item.ean].days_since ? `ingen rörelse ${Math.floor(staleMap[item.ean].days_since / 30)} mån` : 'aldrig såld'}
                            </Badge>
                          )}
                          {totalBooked > 0 && (
                            <Badge 
                              variant="outline" 
                              className="shrink-0 bg-emerald-500/10 border-emerald-500/30 text-emerald-500"
                              data-testid={`available-badge-${item.ean}`}
                            >
                              {available} tillgänglig{available !== 1 ? 'a' : ''}
                            </Badge>
                          )}
                          {totalBooked > 0 && (
                            <Badge 
                              variant="outline" 
                              className="shrink-0 bg-blue-500/10 border-blue-500/30 text-blue-500"
                              data-testid={`booked-badge-${item.ean}`}
                            >
                              {totalBooked} bokad{totalBooked > 1 ? 'e' : ''}
                            </Badge>
                          )}
                          {/* Show booking numbers */}
                          {item.booking_numbers && item.booking_numbers.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              {item.booking_numbers.slice(0, 3).map((num, idx) => (
                                <Badge 
                                  key={idx}
                                  variant="outline" 
                                  className="shrink-0 bg-purple-500/10 border-purple-500/30 text-purple-400 font-mono text-xs"
                                  data-testid={`booking-num-${item.ean}-${idx}`}
                                >
                                  #{num}
                                </Badge>
                              ))}
                              {item.booking_numbers.length > 3 && (
                                <span className="text-xs text-muted-foreground">
                                  +{item.booking_numbers.length - 3} till
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate mb-2">
                          {item.produktnamn}
                        </p>
                        {showPurchasePrices && (
                          <div className="mb-2" data-testid={`purchase-price-${item.ean}`}>
                            {priceMap[item.ean] ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded">
                                Inköpspris: {priceMap[item.ean].price_sek.toLocaleString('sv-SE')} kr
                                {priceMap[item.ean].currency === 'EUR' && (
                                  <span className="font-normal text-emerald-600/70">({priceMap[item.ean].raw} €)</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">Inget inköpspris</span>
                            )}
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          {/* Show all locations with quantities - filter out zero stock */}
                          {item.locations ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {item.locations.filter(loc => loc.antal > 0).length > 0 ? (
                                <>
                                  <MapPin className="h-3 w-3 text-muted-foreground" />
                                  {item.locations.filter(loc => loc.antal > 0).map((loc, idx, arr) => (
                                    <span key={loc.lagerplats} className="inline-flex items-center">
                                      <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-foreground">
                                        {loc.lagerplats}
                                      </span>
                                      <span className="text-muted-foreground ml-0.5">({loc.antal})</span>
                                      {idx < arr.length - 1 && <span className="text-muted-foreground mx-1">•</span>}
                                    </span>
                                  ))}
                                </>
                              ) : (
                                <span className="text-muted-foreground italic">Ingen lagerplats</span>
                              )}
                            </div>
                          ) : item.antal > 0 ? (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                              <span className="font-mono">{item.lagerplats}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground italic">Ingen lagerplats</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          </>
        )}
      </main>
      
      {/* New Product Dialog */}
      <NewProductDialog
        open={showNewProductDialog}
        onOpenChange={handleNewProductDialogClose}
        newProduct={newProduct}
        onProductChange={setNewProduct}
        manufacturers={manufacturers}
        categories={categories}
        onSave={handleSaveNewProduct}
        processing={creatingProduct}
      />
    </div>
  );
}
